import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, TextField, Button, List, ListItem, ListItemText, Autocomplete, Snackbar, Alert } from '@mui/material';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import { MunicipalitiesManagementProps, Municipality } from './type';
import { COLORS_ADMIN_PAGE } from '../conf/constants';
import { useErrorHandler } from '../utils/useErrorHandler';
import debounce from 'lodash/debounce';
import { Province } from '../conf/type';
import dataProvider from '../services/api';

const MunicipalitiesManagement: React.FC<MunicipalitiesManagementProps> = ({
  assignedMunicipalities,
  setAssignedMunicipalities,
  setMunicipalitiesChanged,
  administratorId,
  provinceSearch,
  setProvinceSearch,
}) => {
  const { snackbar, handleErrorWithLogout, showSuccessMessage, showErrorMessage, handleSnackbarClose } = useErrorHandler();
  const [municipalitySearch, setMunicipalitySearch] = useState<Municipality | null>(null);
  const [allMunicipalities, setAllMunicipalities] = useState<Municipality[]>([]);
  const [allMunicipalitiesForAssign, setAllMunicipalitiesForAssign] = useState<Municipality[]>([]);
  const [allProvinces, setAllProvinces] = useState<Province[]>([]);
  const [filteredMunicipalities, setFilteredMunicipalities] = useState<Municipality[]>([]);
  const [selectedMunicipalities, setSelectedMunicipalities] = useState<Set<number>>(new Set());
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [searchCache, setSearchCache] = useState<Map<string, Municipality[]>>(new Map());
  const [page, setPage] = useState(0);
  const [count] = useState(100);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: provinces } = await dataProvider.getAllProvinces('administration');
        setAllProvinces(provinces);
      } catch (error: any) {
        handleErrorWithLogout(error);
      }
    };
    fetchData();
  }, [dataProvider]);

  useEffect(() => {
    const fetchMunicipalities = async () => {
      try {
        const { data } = await dataProvider.getAllMunicipalities('administration', { page, count });
        setAllMunicipalities(data);
        setFilteredMunicipalities(
          data.filter(
            (municipality: Municipality) =>
              !assignedMunicipalities.some((assigned) => assigned.id === municipality.id)
          )
        );
      } catch (error: any) {
        setAllMunicipalities([]);
        setFilteredMunicipalities([]);
        handleErrorWithLogout(error);
      }
    };
    fetchMunicipalities();
  }, [assignedMunicipalities, dataProvider, page, count]);

  useEffect(() => {
    const fetchAllMunicipalitiesForAssign = async () => {
      try {
        let allMunicipalitiesData: Municipality[] = [];
        let currentPage = 0;
        const perPage = 100;

        while (true) {
          const { data } = await dataProvider.getAllMunicipalities('administration', {
            page: currentPage,
            count: perPage,
          });
          allMunicipalitiesData = [...allMunicipalitiesData, ...data];
          if (data.length === 0) break;
          currentPage++;
        }
        setAllMunicipalitiesForAssign(allMunicipalitiesData);
      } catch (error: any) {
        setAllMunicipalitiesForAssign([]);
        handleErrorWithLogout(error);
      }
    };
    fetchAllMunicipalitiesForAssign();
  }, [dataProvider]);

  const fetchSearchResults = useCallback(
    debounce(async (keyword: string) => {
      if (!keyword) {
        setFilteredMunicipalities(
          allMunicipalities.filter(
            (municipality: Municipality) =>
              !assignedMunicipalities.some((assigned) => assigned.id === municipality.id)
          )
        );
        return;
      }

      if (searchCache.has(keyword)) {
        const cachedResults = searchCache.get(keyword)!;
        setFilteredMunicipalities(
          cachedResults.filter(
            (municipality) => !assignedMunicipalities.some((assigned) => assigned.id === municipality.id)
          )
        );
        return;
      }

      try {
        const { data } = await dataProvider.lookupCities('administration', { name: keyword, page, count });
        const municipalities = data || [];
        setSearchCache((prev) => new Map(prev).set(keyword, municipalities));
        setFilteredMunicipalities(
          municipalities.filter(
            (municipality: Municipality) =>
              !assignedMunicipalities.some((assigned) => assigned.id === municipality.id)
          )
        );
      } catch (error: any) {
        setFilteredMunicipalities([]);
        handleErrorWithLogout(error);
      }
    }, 300),
    [allMunicipalities, assignedMunicipalities, dataProvider, searchCache, page]
  );

  useEffect(() => {
    fetchSearchResults(searchKeyword);
  }, [searchKeyword, fetchSearchResults]);

  const handleMunicipalityChange = async (event: React.SyntheticEvent, value: Municipality | null) => {
    if (value && administratorId !== undefined) {
      try {
        const currentMunicipalityIds = assignedMunicipalities.map((m) => m.id);
        const newMunicipalityId = value.id;
        const updatedMunicipalityIds: number[] = [...currentMunicipalityIds, newMunicipalityId];

        await dataProvider.assignMunicipalities({ id: administratorId, municipalityIds: updatedMunicipalityIds,});

        const { data: refreshedMunicipalities } = await dataProvider.getAssignedMunicipalities({ id: administratorId});
        setAssignedMunicipalities(refreshedMunicipalities);
        setMunicipalitySearch(null);
        setMunicipalitiesChanged(true);
        showSuccessMessage('Municipality assigned successfully!');
      } catch (error: any) {
        handleErrorWithLogout(error);
      }
    } else {
      setMunicipalitySearch(value);
    }
  };

  const handleProvinceChange = async (event: React.SyntheticEvent, value: Province | null) => {
    setProvinceSearch(value);
    setMunicipalitySearch(null);

    if (value && administratorId !== undefined) {
      try {
        const { data: provinceMunicipalities } = await dataProvider.getCitiesByProvince('administration/address', {
          provinceId: value.id,
          page,
          count,
        });
        const municipalityIds = provinceMunicipalities.map((municipality: Municipality) => municipality.id);
        const currentMunicipalityIds = assignedMunicipalities.map((m) => m.id);
        const updatedMunicipalityIds = [...currentMunicipalityIds, ...municipalityIds];

        await dataProvider.assignMunicipalities({ id: administratorId, municipalityIds: updatedMunicipalityIds});

        const { data: refreshedMunicipalities } = await dataProvider.getAssignedMunicipalities({id: administratorId});
        setAssignedMunicipalities(refreshedMunicipalities);
        setMunicipalitiesChanged(true);
        showSuccessMessage(`All municipalities in ${value.name} assigned successfully!`);
      } catch (error: any) {
        handleErrorWithLogout(error);
      }
    }
  };

  const handleAssignAllMunicipalitiesForAssignAll = async () => {
    if (!administratorId) {
      showErrorMessage('Administrator ID is missing.');
      return;
    }

    try {
      const allMunicipalityIds = allMunicipalitiesForAssign.map((m) => m.id);
      const currentMunicipalityIds = assignedMunicipalities.map((m) => m.id);
      const updatedMunicipalityIds = [...currentMunicipalityIds, ...allMunicipalityIds];

      await dataProvider.assignMunicipalities({
        id: administratorId,
        municipalityIds: updatedMunicipalityIds,
      });

      const { data: refreshedMunicipalities } = await dataProvider.getAssignedMunicipalities({
        id: administratorId,
      });
      setAssignedMunicipalities(refreshedMunicipalities);
      setMunicipalitiesChanged(true);
      showSuccessMessage('All available municipalities assigned successfully!');
    } catch (error: any) {
      handleErrorWithLogout(error);
    }
  };

  const handleToggleSelect = (municipalityId: number) => {
    const newSelected = new Set(selectedMunicipalities);
    if (newSelected.has(municipalityId)) {
      newSelected.delete(municipalityId);
    } else {
      newSelected.add(municipalityId);
    }
    setSelectedMunicipalities(newSelected);
  };

  const handleRemoveSelected = async () => {
    if (selectedMunicipalities.size === 0 || !administratorId) {
      showErrorMessage('Please select at least one municipality to remove.');
      return;
    }

    try {
      const remainingMunicipalities = assignedMunicipalities.filter(
        (municipality) => !selectedMunicipalities.has(municipality.id)
      );
      const remainingMunicipalityIds = remainingMunicipalities.map((m) => m.id);

      await dataProvider.assignMunicipalities({
        id: administratorId,
        municipalityIds: remainingMunicipalityIds,
      });

      const { data: refreshedMunicipalities } = await dataProvider.getAssignedMunicipalities({
        id: administratorId,
      });
      setAssignedMunicipalities(refreshedMunicipalities);
      setSelectedMunicipalities(new Set());
      setMunicipalitiesChanged(true);
      showSuccessMessage(`${selectedMunicipalities.size} municipality(ies) removed successfully!`);
    } catch (error: any) {
      handleErrorWithLogout(error);
    }
  };

  const sortedMunicipalities = [...assignedMunicipalities].sort((a, b) => {
    if (a.name === 'All' || a.name === 'All Municipalities') return -1;
    if (b.name === 'All' || b.name === 'All Municipalities') return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <Box sx={{ mt: 3, display: 'flex' }}>
      <Box sx={{ flex: 1, mr: 2 }}>
        <Typography variant="h6" gutterBottom>
          Assigned Municipalities
        </Typography>
        <Box
          sx={{
            height: 300,
            overflow: 'auto',
            p: 1,
            bgcolor: 'background.paper',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <List>
            {sortedMunicipalities.length === 0 ? (
              <ListItem>
                <ListItemText primary="No municipalities assigned" />
              </ListItem>
            ) : (
              sortedMunicipalities.map((municipality) => (
                <ListItem
                  key={municipality.id}
                  dense
                  onClick={() => handleToggleSelect(municipality.id)}
                  sx={{
                    bgcolor: selectedMunicipalities.has(municipality.id)
                      ? COLORS_ADMIN_PAGE.PRIMARY
                      : 'transparent',
                    color: selectedMunicipalities.has(municipality.id)
                      ? COLORS_ADMIN_PAGE.WHITE
                      : 'black',
                    cursor: 'pointer',
                    paddingY: 2,
                  }}
                >
                  <LocationCityIcon
                    sx={{
                      mr: 1,
                      color: selectedMunicipalities.has(municipality.id)
                        ? COLORS_ADMIN_PAGE.WHITE
                        : COLORS_ADMIN_PAGE.PRIMARY,
                    }}
                  />
                  <Typography sx={{ mb: 0 }} gutterBottom>
                    {municipality.province.name} - {municipality.name}
                  </Typography>
                </ListItem>
              ))
            )}
          </List>
        </Box>
      </Box>

      <Box sx={{ flex: 1, alignSelf: 'flex-end' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Add Municipality
          </Typography>
          <Autocomplete
            options={filteredMunicipalities}
            getOptionLabel={(option) => option.province.name + ' - ' + option.name}
            value={municipalitySearch}
            onChange={handleMunicipalityChange}
            onInputChange={(event, newInputValue) => {
              setSearchKeyword(newInputValue);
            }}
            disabled={filteredMunicipalities.length === 0 && !searchKeyword}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Search Municipality"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: COLORS_ADMIN_PAGE.PRIMARY,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: COLORS_ADMIN_PAGE.PRIMARY,
                    },
                  },
                }}
              />
            )}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Add Province
          </Typography>
          <Autocomplete
            options={allProvinces}
            getOptionLabel={(option) => option.name}
            value={provinceSearch}
            onChange={handleProvinceChange}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Search Province"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: COLORS_ADMIN_PAGE.PRIMARY,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: COLORS_ADMIN_PAGE.PRIMARY,
                    },
                  },
                }}
              />
            )}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<LocationCityIcon />}
            onClick={handleAssignAllMunicipalitiesForAssignAll}
            sx={{ bgcolor: COLORS_ADMIN_PAGE.PRIMARY }}
          >
            ASSIGN ALL MUNICIPALITIES
          </Button>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleRemoveSelected}
            startIcon={<RemoveCircleOutline />}
            disabled={selectedMunicipalities.size === 0}
            sx={{ bgcolor: COLORS_ADMIN_PAGE.PRIMARY }}
          >
            REMOVE SELECTED ASSIGNMENTS
          </Button>
        </Box>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MunicipalitiesManagement;